// ============================================================
// DetailDrawer — Side-Panel für Eintragsdetails
// ============================================================

import { useEffect } from 'react';
import { Icon } from '../icons';
import { CO } from '../theme';
import { dstr, eur, sEur, tstr } from '../format';
import { StatusBadge } from './ui';
import { useApp, useEntries } from '../store';

export function DetailDrawer() {
  const { detailId, setDetailId } = useApp();
  const entries = useEntries();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDetailId(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setDetailId]);

  if (!detailId) return null;
  const e = entries.find((x) => x.id === detailId);
  if (!e) return null;
  const net = e.gewinn - e.einsatz;

  function row(l: string, v: React.ReactNode, mono = false) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          padding: '11px 0',
          borderTop: `1px solid ${CO.border2}`,
        }}
      >
        <span style={{ fontSize: 12.5, color: CO.muted }}>{l}</span>
        <span
          className={mono ? 'num' : ''}
          style={{ fontSize: 13, color: CO.text, fontWeight: 500, textAlign: 'right' }}
        >
          {v}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div onClick={() => setDetailId(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(20,25,20,.28)' }} />
      <div
        style={{
          position: 'relative',
          width: 420,
          maxWidth: '92vw',
          background: '#fff',
          height: '100%',
          boxShadow: '-14px 0 40px rgba(0,0,0,.16)',
          animation: 'wlSlide .24s ease',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${CO.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <div style={{ fontSize: 11.5, color: CO.faint }}>Eintragsdetails</div>
            <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>{e.spielart}</div>
          </div>
          <button
            onClick={() => setDetailId(null)}
            aria-label="Schließen"
            style={{
              border: 'none',
              background: '#f0f2ed',
              borderRadius: 8,
              padding: 7,
              lineHeight: 0,
              cursor: 'pointer',
            }}
          >
            <Icon name="x" color={CO.muted} size={18} />
          </button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
            <div style={{ background: '#f6f7f4', borderRadius: 10, padding: '12px' }}>
              <div style={{ fontSize: 11, color: CO.faint }}>Einsatz</div>
              <div className="num" style={{ fontSize: 16, fontWeight: 700, color: CO.einsatz }}>{eur(e.einsatz)}</div>
            </div>
            <div style={{ background: '#f6f7f4', borderRadius: 10, padding: '12px' }}>
              <div style={{ fontSize: 11, color: CO.faint }}>Auszahlung</div>
              <div className="num" style={{ fontSize: 16, fontWeight: 700, color: CO.auszahlung }}>{eur(e.gewinn)}</div>
            </div>
            <div
              style={{
                background: net >= 0 ? '#f1f8f2' : '#fbeeed',
                borderRadius: 10,
                padding: '12px',
              }}
            >
              <div style={{ fontSize: 11, color: CO.faint }}>Nettoergebnis</div>
              <div
                className="num"
                style={{ fontSize: 16, fontWeight: 700, color: net >= 0 ? CO.pos : CO.neg }}
              >
                {sEur(net)}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            {row('Vollständiges Datum', `${dstr(e.iso)}, ${tstr(e.iso)} Uhr`, true)}
            {row('Spielart', e.spielart)}
            {row('Status', <StatusBadge s={e.status} />)}
            {row('Ergebnistyp', e.typ.charAt(0).toUpperCase() + e.typ.slice(1))}
            {row('Ursprünglicher Ergebnistext', e.rohtext, true)}
            {row('Eintrags-ID', e.id, true)}
          </div>
          {e.detailUrl ? (
            <a
              href={e.detailUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                marginTop: 18,
                padding: '11px',
                border: `1px solid ${CO.border}`,
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                color: CO.sidebar,
                textDecoration: 'none',
              }}
            >
              <Icon name="ext" color={CO.sidebar} size={16} />
              Detailseite öffnen (extern)
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}
